/* ============================================================
   TMS Stock Notify — sends a back-in-stock request to Eric's
   contact-form inbox.
   - Logged-in user: skip the drawer, submit inline, show success below.
   - Guest: open right-side drawer with email field, submit, auto-close.
   - Subscribed variant IDs are persisted in localStorage so the
     "already subscribed" state survives page refresh + variant change.
============================================================ */

const TMS_STOCK_NOTIFY_STORAGE_KEY = 'tms_stock_notify_subscribed_v1';
const TMS_STOCK_NOTIFY_CHANGE_EVENT = 'tms-stock-notify:change';

const TmsStockNotifyStore = {
  read() {
    try {
      const raw = localStorage.getItem(TMS_STOCK_NOTIFY_STORAGE_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.map(String) : [];
    } catch (e) {
      return [];
    }
  },

  write(ids) {
    try {
      localStorage.setItem(TMS_STOCK_NOTIFY_STORAGE_KEY, JSON.stringify(ids));
    } catch (e) {
      // localStorage may be unavailable (private mode, quota) — fail silent;
      // the UI just won't persist across reloads. Submission itself still works.
    }
  },

  has(variantId) {
    if (!variantId) return false;
    return this.read().includes(String(variantId));
  },

  add(variantId) {
    if (!variantId) return;
    const ids = this.read();
    const id = String(variantId);
    if (ids.includes(id)) return;
    ids.push(id);
    this.write(ids);
    window.dispatchEvent(new CustomEvent(TMS_STOCK_NOTIFY_CHANGE_EVENT, { detail: { variantId: id } }));
  }
};

class TmsStockNotify extends HTMLElement {
  static AUTO_CLOSE_MS = 2500;

  connectedCallback() {
    this.trigger = this.querySelector('[data-tms-stock-notify-open]');
    this.inlineSuccess = this.querySelector('[data-tms-stock-notify-inline-success]');

    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.productTitle = this.dataset.productTitle;
    this.variantTitle = this.dataset.variantTitle;
    this.optionNames = (this.dataset.optionNames || '').split('|').filter(Boolean);
    this.variantOptions = (this.dataset.variantOptions || '').split('|').filter(Boolean);
    this.customerEmail = this.dataset.customerEmail || '';

    this.buyButtons = this.parentElement?.querySelector('[data-tms-buy-target]');
    this.productInfo = this.closest('product-info');

    this.trigger?.addEventListener('click', this.onTriggerClick.bind(this));

    // Show the "already subscribed" state on initial render if this variant
    // is in localStorage.
    this.syncSubscriptionUI();

    // Re-sync when another <tms-stock-notify> in this tab subscribes (e.g.
    // quick-view modal) or when another tab writes to localStorage.
    this.onStoreChange = this.syncSubscriptionUI.bind(this);
    window.addEventListener(TMS_STOCK_NOTIFY_CHANGE_EVENT, this.onStoreChange);
    this.onStorageEvent = (event) => {
      if (event.key === TMS_STOCK_NOTIFY_STORAGE_KEY) this.syncSubscriptionUI();
    };
    window.addEventListener('storage', this.onStorageEvent);

    // Fetch product variants once so we can resolve full variant data on
    // change without depending on Dawn's pubsub timing.
    this.variantsByIdPromise = this.loadVariants();

    // Watch the form's hidden variant id input — Dawn updates this on every
    // variant change. MutationObserver is more reliable than the pub/sub
    // subscription which can miss events if scripts load out of order.
    this.bindVariantWatcher();
  }

  syncSubscriptionUI() {
    if (TmsStockNotifyStore.has(this.variantId)) {
      this.applySubscribedUI();
    } else {
      this.applyDefaultUI();
    }
  }

  applySubscribedUI() {
    this.trigger?.setAttribute('hidden', '');
    this.trigger?.removeAttribute('aria-disabled');
    this.trigger?.classList.remove('is-loading');
    this.inlineSuccess?.removeAttribute('hidden');
  }

  applyDefaultUI() {
    this.trigger?.removeAttribute('hidden');
    this.trigger?.removeAttribute('aria-disabled');
    this.trigger?.classList.remove('is-loading');
    this.inlineSuccess?.setAttribute('hidden', '');
  }

  async loadVariants() {
    try {
      const handle = this.productInfo?.dataset?.url?.split('/').pop()
        || window.location.pathname.split('/products/').pop().split('?')[0].split('/')[0];
      if (!handle) return {};
      const resp = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
      if (!resp.ok) return {};
      const data = await resp.json();
      const map = {};
      (data.variants || []).forEach((v) => { map[String(v.id)] = v; });
      return map;
    } catch (e) {
      return {};
    }
  }

  bindVariantWatcher() {
    const variantInput = this.productInfo?.querySelector('[name="id"]')
      || document.querySelector(`product-form [name="id"]`);
    if (!variantInput) return;
    this.variantInput = variantInput;
    this.lastSeenVariantId = String(variantInput.value || '');

    // Bubble-phase change listener (in case Dawn dispatches one)
    variantInput.addEventListener('change', () => this.handleVariantInputChange(variantInput.value));

    // Belt-and-braces: poll the input value every 250ms. Variant changes set
    // the .value property which doesn't always trigger DOM events. This is
    // cheap (single property read) and fires within 250ms of any variant pick.
    this.variantPollHandle = window.setInterval(() => {
      if (!this.variantInput) return;
      const current = String(this.variantInput.value || '');
      if (current && current !== this.lastSeenVariantId) {
        this.lastSeenVariantId = current;
        this.handleVariantInputChange(current);
      }
    }, 250);
  }

  async handleVariantInputChange(newVariantId) {
    if (!newVariantId || String(newVariantId) === String(this.variantId)) return;
    const map = await this.variantsByIdPromise;
    const variant = map[String(newVariantId)];
    if (!variant) return;
    this.onVariantChange({ data: { variant } });
  }

  disconnectedCallback() {
    if (this.variantPollHandle) {
      window.clearInterval(this.variantPollHandle);
      this.variantPollHandle = null;
    }
    if (this.onStoreChange) {
      window.removeEventListener(TMS_STOCK_NOTIFY_CHANGE_EVENT, this.onStoreChange);
    }
    if (this.onStorageEvent) {
      window.removeEventListener('storage', this.onStorageEvent);
    }
  }

  onVariantChange(event) {
    const variant = event?.data?.variant;
    if (!variant) return;

    // Sync our internal state + data attrs so the drawer reads fresh info
    this.variantId = String(variant.id);
    this.variantTitle = variant.title === 'Default Title' ? '' : variant.title;
    this.variantOptions = Array.isArray(variant.options) ? variant.options : [];
    this.dataset.variantId = this.variantId;
    this.dataset.variantTitle = this.variantTitle;
    this.dataset.variantOptions = this.variantOptions.join('|');

    // Pick the right UI for the new variant: subscribed → success message,
    // not subscribed → fresh trigger. This replaces the old blanket reset.
    this.syncSubscriptionUI();

    if (variant.available) {
      this.setAttribute('hidden', '');
      this.buyButtons?.removeAttribute('hidden');
    } else {
      this.removeAttribute('hidden');
      this.buyButtons?.setAttribute('hidden', '');
    }
  }

  formatOptions() {
    if (!this.optionNames.length || !this.variantOptions.length) {
      return this.variantTitle || '';
    }
    return this.optionNames
      .map((name, i) => {
        const value = this.variantOptions[i];
        // Skip Shopify's single-variant sentinel so single-variant products
        // don't render a meaningless "Title: Default Title" line.
        if (!value || value === 'Default Title') return null;
        return `${name}: ${value}`;
      })
      .filter(Boolean)
      .join(' · ');
  }

  // Both logged-in and guest customers now go through the drawer. A single
  // visible path means every submission is a real click on the drawer's submit
  // button — the user gesture Shopify's hCaptcha hook expects — and the
  // customer always has context for why a verification challenge may appear.
  onTriggerClick(event) {
    event.preventDefault();
    this.openDrawer();
  }

  openDrawer() {
    const drawer = document.querySelector('tms-stock-notify-drawer');
    if (!drawer) {
      window.location.href = `${window.Shopify?.routes?.root || '/'}contact`;
      return;
    }
    drawer.open({
      opener: this.trigger,
      email: this.customerEmail,
      productId: this.productId,
      variantId: this.variantId,
      productTitle: this.productTitle,
      variantTitle: this.variantTitle,
      variantOptionsLabel: this.formatOptions(),
      onSuccess: () => {
        TmsStockNotifyStore.add(this.variantId);
        this.applySubscribedUI();
      }
    });
  }
}

if (!customElements.get('tms-stock-notify')) {
  customElements.define('tms-stock-notify', TmsStockNotify);
}

class TmsStockNotifyDrawer extends HTMLElement {
  static AUTO_CLOSE_MS = 2500;
  static ANIMATION_MS = 350;
  // Long safety net ONLY. Shopify protects the contact form with hCaptcha,
  // which can present an interactive challenge the customer must solve by hand
  // — easily longer than a few seconds. We must not race it with a short timer
  // (the old 15s deadline was the root cause of the "Something went wrong"
  // bug). Success/failure is decided by the real iframe load; this timer only
  // releases a genuinely abandoned submission after two minutes.
  static SUBMIT_SAFETY_TIMEOUT_MS = 120000;

  connectedCallback() {
    this.panel = this.querySelector('.tms-notify-drawer__panel');
    this.form = this.querySelector('[data-tms-notify-form]');
    this.iframe = this.querySelector('[data-tms-notify-iframe]');
    this.emailInput = this.querySelector('[data-tms-notify-email]');
    this.nameInput = this.querySelector('[data-tms-notify-name]');
    this.bodyInput = this.querySelector('[data-tms-notify-body]');
    this.productText = this.querySelector('[data-tms-notify-product]');
    this.variantText = this.querySelector('[data-tms-notify-variant]');
    this.errorEl = this.querySelector('[data-tms-notify-error]');
    this.successEl = this.querySelector('[data-tms-notify-success]');
    this.verifyingEl = this.querySelector('[data-tms-notify-verifying]');
    this.submitBtn = this.querySelector('[data-tms-notify-submit]');
    this.previousBodyOverflow = '';
    this.context = null;
    this.isSubmitting = false;
    this.submitTimeoutHandle = null;

    // Don't preventDefault the form submit — we WANT the native submit to
    // run so Shopify's invisible-CAPTCHA hook can inject its token onto the
    // form before it posts to the hidden iframe.
    this.form?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.iframe?.addEventListener('load', this.onIframeLoad.bind(this));
    this.emailInput?.addEventListener('input', this.onEmailInput.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('keydown', this.onKeydown.bind(this));
  }

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
  }

  buildHeadline(productTitle, variantTitle) {
    return variantTitle
      ? `Back-in-stock: ${productTitle} — ${variantTitle}`
      : `Back-in-stock: ${productTitle}`;
  }

  buildBody({ email, productTitle, variantTitle, productId, variantId }) {
    return [
      'A customer has requested a back-in-stock notification.',
      '',
      `Product: ${productTitle}`,
      variantTitle ? `Variant: ${variantTitle}` : null,
      `Product ID: ${productId}`,
      `Variant ID: ${variantId}`,
      `Customer email: ${email}`
    ]
      .filter((line) => line !== null)
      .join('\n');
  }

  fillFormFields({ email, productTitle, variantTitle, productId, variantId }) {
    if (this.emailInput && email != null) this.emailInput.value = email;
    if (this.nameInput) this.nameInput.value = this.buildHeadline(productTitle, variantTitle);
    if (this.bodyInput) this.bodyInput.value = this.buildBody({ email, productTitle, variantTitle, productId, variantId });
  }

  onEmailInput() {
    if (!this.submitBtn) return;
    const valid = this.isValidEmail(this.emailInput?.value);
    this.submitBtn.disabled = !valid;
    if (valid) this.errorEl?.setAttribute('hidden', '');
  }

  onClick(event) {
    if (event.target.closest('[data-tms-notify-close]')) {
      this.close();
    }
  }

  onKeydown(event) {
    if (this.hasAttribute('hidden')) return;
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'Tab') {
      this.trapTab(event);
    }
  }

  // Keep keyboard focus inside the drawer. Focusables are recomputed on each Tab
  // because the submit button starts disabled and enables once the email is
  // valid — a first/last snapshot taken at open time would miss it.
  trapTab(event) {
    const focusables = Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const outside = !this.panel.contains(active);
    if (event.shiftKey && (active === first || active === this.panel || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault();
      first.focus();
    }
  }

  open(context) {
    this.context = context;
    this.openedBy = context.opener || document.activeElement;
    this.errorEl?.setAttribute('hidden', '');
    this.successEl?.setAttribute('hidden', '');
    this.verifyingEl?.setAttribute('hidden', '');
    this.form?.removeAttribute('hidden');
    if (this.form) this.form.reset();
    if (this.submitBtn) this.submitBtn.disabled = true;

    if (this.productText) {
      this.productText.textContent = context.productTitle || '';
    }
    if (this.variantText) {
      this.variantText.textContent = context.variantOptionsLabel || '';
    }

    // Pre-fill a known (logged-in) email but keep it editable, so a customer
    // can send the alert to a different address. onEmailInput() enables submit.
    if (context.email && this.emailInput) {
      this.emailInput.value = context.email;
      this.onEmailInput();
    }

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.removeAttribute('hidden');
    requestAnimationFrame(() => this.classList.add('is-open'));
    setTimeout(() => this.emailInput?.focus({ preventScroll: true }), 50);
  }

  close() {
    if (this.hasAttribute('hidden')) return;

    // Cancel any in-flight submit so re-opening (or retrying) starts clean.
    // Since we no longer auto-fail on a short timer, the user must always be
    // able to back out of a pending verification themselves.
    if (this.isSubmitting) {
      this.isSubmitting = false;
      if (this.submitTimeoutHandle) {
        window.clearTimeout(this.submitTimeoutHandle);
        this.submitTimeoutHandle = null;
      }
      this.submitBtn?.classList.remove('is-loading');
      this.submitBtn?.removeAttribute('aria-disabled');
      this.verifyingEl?.setAttribute('hidden', '');
    }

    this.classList.remove('is-open');
    document.body.style.overflow = this.previousBodyOverflow;

    const finalize = () => {
      this.setAttribute('hidden', '');
      this.context = null;
      // Return focus to whatever opened the drawer (e.g. the "Notify me" button).
      if (this.openedBy && typeof this.openedBy.focus === 'function') this.openedBy.focus();
      this.openedBy = null;
    };
    this.panel.addEventListener('transitionend', finalize, { once: true });
    setTimeout(finalize, TmsStockNotifyDrawer.ANIMATION_MS + 50);
  }

  onFormSubmit(event) {
    const email = this.emailInput?.value?.trim();
    if (!this.isValidEmail(email)) {
      // Block submit — invalid email shouldn't hit the network.
      event.preventDefault();
      this.showError(window.TmsNotifyStrings?.invalidEmail || 'Please enter a valid email address.');
      return;
    }
    this.fillFormFields({
      email,
      productTitle: this.context?.productTitle,
      variantTitle: this.context?.variantTitle,
      productId: this.context?.productId,
      variantId: this.context?.variantId
    });

    // Do NOT preventDefault here — the native submit must run so Shopify's
    // hCaptcha hook can inject its token and post into the hidden iframe. If a
    // challenge is required, hCaptcha renders its own overlay on top of the
    // page; the customer solves it and the form then posts. We just wait.
    this.isSubmitting = true;
    this.errorEl?.setAttribute('hidden', '');
    this.verifyingEl?.removeAttribute('hidden');
    this.submitBtn?.setAttribute('aria-disabled', 'true');
    this.submitBtn?.classList.add('is-loading');

    // Long safety net only — releases a genuinely stuck state (e.g. the user
    // abandons the challenge). The real result comes from onIframeLoad(); we
    // never race the captcha with a short deadline.
    this.submitTimeoutHandle = window.setTimeout(() => {
      if (this.isSubmitting) this.handleResult(false);
    }, TmsStockNotifyDrawer.SUBMIT_SAFETY_TIMEOUT_MS);
  }

  onIframeLoad() {
    // The iframe fires `load` on the initial about:blank too. Ignore unless
    // we're mid-submission.
    if (!this.isSubmitting) return;
    if (this.submitTimeoutHandle) {
      window.clearTimeout(this.submitTimeoutHandle);
      this.submitTimeoutHandle = null;
    }

    // Default to optimistic success: any iframe load that fires after submit
    // means Shopify processed the request. Shopify's post-submit redirect
    // chain can land the iframe on a cross-origin URL (CDN / shop subdomain),
    // which would make contentWindow.location.href throw a SecurityError. We
    // only flip to failure when we can DEFINITIVELY read the URL AND see
    // it's the contact page without the success param.
    let ok = true;
    try {
      const href = this.iframe?.contentWindow?.location?.href || null;
      if (href && !href.startsWith('about:')) {
        // A captcha challenge rendered inside the hidden iframe can't be seen or
        // solved by the customer, so it's a stuck submission — not a success.
        if (href.includes('/challenge')) {
          ok = false;
        } else if (href.includes('/contact') && !href.includes('contact_posted=true')) {
          ok = false;
        }
      }
    } catch (e) {
      // Cross-origin read blocked — treat as success per the optimistic default.
    }

    this.handleResult(ok);
  }

  handleResult(ok) {
    this.isSubmitting = false;
    if (this.submitTimeoutHandle) {
      window.clearTimeout(this.submitTimeoutHandle);
      this.submitTimeoutHandle = null;
    }
    this.submitBtn?.classList.remove('is-loading');
    this.submitBtn?.removeAttribute('aria-disabled');
    this.verifyingEl?.setAttribute('hidden', '');

    if (ok) {
      this.form?.setAttribute('hidden', '');
      this.successEl?.removeAttribute('hidden');
      this.context?.onSuccess?.();
      setTimeout(() => this.close(), TmsStockNotifyDrawer.AUTO_CLOSE_MS);
    } else {
      this.showError(window.TmsNotifyStrings?.submitError || 'Something went wrong. Please try again.');
    }
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.removeAttribute('hidden');
  }
}

if (!customElements.get('tms-stock-notify-drawer')) {
  customElements.define('tms-stock-notify-drawer', TmsStockNotifyDrawer);
}
